/**
 * RecordRun - Modal for recording/logging runs
 * Follows CreateReport.tsx pattern
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useAirQualityStore } from '@/stores/useAirQualityStore';
import { cn } from '@/utils/cn';
import type { ActivityType, CreateActivityData, RoutePoint, AQISample } from '@/types/running';

interface RecordRunProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function RecordRun({ isOpen, onClose, onSuccess }: RecordRunProps) {
  const { createActivity, isRecording, startRecording, stopRecording, clearRecordingData, recordingData, addRoutePoint, addAQISample } = useRunningStore();
  const currentPosition = useLocationStore((state) => state.currentPosition);
  const airQuality = useAirQualityStore((state) => state.currentReading);

  const [mode, setMode] = useState<'manual' | 'tracking'>('manual');
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  // Preserve route/AQI data after stopRecording (so they survive state clearing)
  const [savedRoute, setSavedRoute] = useState<RoutePoint[]>([]);
  const [savedAqiSamples, setSavedAqiSamples] = useState<AQISample[]>([]);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [photoTemplate, setPhotoTemplate] = useState<'original' | 'blurpie'>('blurpie');
  const [photoOrientation, setPhotoOrientation] = useState<'landscape' | 'portrait' | 'square'>('landscape');
  const [isSharing, setIsSharing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    activity_type: ActivityType;
    distance_km: string;
    hours: string;
    minutes: string;
    seconds: string;
    elevation_gain: string;
    visibility: 'public' | 'followers' | 'private';
  }>({
    title: '',
    description: '',
    activity_type: 'run',
    distance_km: '',
    hours: '0',
    minutes: '',
    seconds: '0',
    elevation_gain: '0',
    visibility: 'public',
  });

  // Photo handling
  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result as string;
        // Detect image orientation
        const img = new Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          if (ratio > 1.1) {
            setPhotoOrientation('landscape');
          } else if (ratio < 0.9) {
            setPhotoOrientation('portrait');
          } else {
            setPhotoOrientation('square');
          }
          setPhotoPreview(dataUrl);
          setShowPhotoOptions(false);
          // Go directly to template view (default)
          setPhotoTemplate('blurpie');
        };
        img.src = dataUrl;
      };
      reader.readAsDataURL(file);
    }
  };

  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  const openGallery = () => {
    fileInputRef.current?.click();
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setPhotoTemplate('blurpie');
    setPhotoOrientation('landscape');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  // Share/Save photo handler
  const handleSharePhoto = async () => {
    if (!photoPreview) return;
    setIsSharing(true);

    try {
      // Convert data URL to blob
      const response = await fetch(photoPreview);
      const blob = await response.blob();
      const file = new File([blob], 'blurpie-run.png', { type: 'image/png' });

      // Check if Web Share API is available
      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: formData.title || 'My Run on Blurp.ie',
          text: `${displayName || 'Runner'} just completed a ${formData.distance_km || '0'}km run! Check out Blurp.ie for air-quality aware running.`,
          files: [file],
        });
      } else {
        // Fallback: download the image
        const link = document.createElement('a');
        link.href = photoPreview;
        link.download = `blurpie-run-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      // User cancelled or error occurred
      console.log('Share cancelled or failed:', error);
    } finally {
      setIsSharing(false);
    }
  };

  // Live tracking effect
  useEffect(() => {
    let watchId: number | null = null;
    let aqiInterval: ReturnType<typeof setInterval> | null = null;

    if (isRecording && 'geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          addRoutePoint({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            elevation: position.coords.altitude || undefined,
            timestamp: new Date().toISOString(),
          });
        },
        (error) => console.error('GPS error:', error),
        { enableHighAccuracy: true, maximumAge: 1000 }
      );

      // Sample AQI every 30 seconds
      aqiInterval = setInterval(() => {
        if (currentPosition && airQuality) {
          addAQISample({
            timestamp: new Date().toISOString(),
            lat: currentPosition.lat,
            lng: currentPosition.lng,
            aqi: airQuality.aqi || 0,
            pm25: airQuality.pm25?.value || null,
            temperature: null,
            humidity: null,
          });
        }
      }, 30000);
    }

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (aqiInterval) {
        clearInterval(aqiInterval);
      }
    };
  }, [isRecording, currentPosition, airQuality, addRoutePoint, addAQISample]);

  const handleStartTracking = () => {
    setMode('tracking');
    startRecording();
  };

  const handleStopTracking = () => {
    // Save route and AQI data BEFORE stopping (critical: prevents data loss)
    setSavedRoute([...recordingData.route]);
    setSavedAqiSamples([...recordingData.aqi_samples]);
    const savedDistance = recordingData.distance;

    const data = stopRecording();
    if (data) {
      // Pre-fill form with recorded data
      const distanceKm = savedDistance / 1000;
      const durationSeconds = data.duration || 0;
      const hours = Math.floor(durationSeconds / 3600);
      const mins = Math.floor((durationSeconds % 3600) / 60);
      const secs = durationSeconds % 60;

      setFormData((prev) => ({
        ...prev,
        distance_km: distanceKm.toFixed(2),
        hours: hours.toString(),
        minutes: mins.toString(),
        seconds: secs.toString(),
      }));
    }
    setMode('manual');
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      setSubmitError('Please enter your display name');
      return;
    }

    if (!formData.title.trim()) {
      setSubmitError('Please add a title for your activity');
      return;
    }

    const distanceMeters = parseFloat(formData.distance_km || '0') * 1000;
    const durationSeconds =
      parseInt(formData.hours || '0') * 3600 +
      parseInt(formData.minutes || '0') * 60 +
      parseInt(formData.seconds || '0');

    if (distanceMeters <= 0) {
      setSubmitError('Please enter a valid distance');
      return;
    }

    if (durationSeconds <= 0) {
      setSubmitError('Please enter a valid duration');
      return;
    }

    setSubmitError(null);
    setIsSaving(true);

    // Use saved route/AQI data (from handleStopTracking), fall back to current recordingData
    const routeData = savedRoute.length > 0 ? savedRoute : (recordingData.route || []);
    const aqiData = savedAqiSamples.length > 0 ? savedAqiSamples : (recordingData.aqi_samples || []);

    const activityData: CreateActivityData = {
      title: formData.title,
      description: `${displayName.trim()}${formData.description ? '\n' + formData.description : ''}`,
      activity_type: formData.activity_type,
      route_polyline: routeData,
      start_location: currentPosition ? { lat: currentPosition.lat, lng: currentPosition.lng } : undefined,
      distance_meters: distanceMeters,
      duration_seconds: durationSeconds,
      elevation_gain_meters: parseInt(formData.elevation_gain || '0'),
      aqi_samples: aqiData,
      visibility: formData.visibility,
      photos: photoPreview ? [photoPreview] : [],
      display_name: displayName.trim(),
    };

    try {
      await createActivity(activityData);

      // Only show success AFTER the database confirms the save
      clearRecordingData();
      setShowSuccess(true);
      onSuccess?.();

      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        resetForm();
      }, 1500);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to save activity. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      activity_type: 'run',
      distance_km: '',
      hours: '0',
      minutes: '',
      seconds: '0',
      elevation_gain: '0',
      visibility: 'public',
    });
    setDisplayName('');
    setPhotoPreview(null);
    setMode('manual');
    setSubmitError(null);
    setIsSaving(false);
    setSavedRoute([]);
    setSavedAqiSamples([]);
  };

  const activityTypes: { value: ActivityType; label: string; emoji: string }[] = [
    { value: 'run', label: 'Run', emoji: '🏃' },
    { value: 'walk', label: 'Walk', emoji: '🚶' },
    { value: 'hike', label: 'Hike', emoji: '🥾' },
    { value: 'trail_run', label: 'Trail Run', emoji: '🌲' },
    { value: 'treadmill', label: 'Treadmill', emoji: '🏋️' },
  ];

  const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={cn(
              'relative w-full sm:max-w-md bg-app-bg border border-glass-border',
              'rounded-t-3xl sm:rounded-2xl overflow-hidden',
              'max-h-[90vh] overflow-y-auto'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-app-bg/90 backdrop-blur-xl border-b border-glass-border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <span>🏃</span> {isRecording ? 'Recording...' : 'Log Activity'}
                  </h2>
                  <p className="text-xs text-text-secondary mt-1">
                    {isRecording ? 'GPS tracking active' : 'Record or manually enter your run'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-text-secondary hover:text-white transition-colors text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Error message */}
              {submitError && (
                <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-xs text-center">{submitError}</p>
                </div>
              )}
            </div>

            {/* Success overlay */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute inset-0 bg-app-bg/95 backdrop-blur-xl flex items-center justify-center z-10"
                >
                  <div className="text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.1 }}
                      className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center"
                    >
                      <span className="text-4xl">🎉</span>
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-2">Activity Saved!</h3>
                    <p className="text-text-secondary text-sm">Great run! Keep it up!</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Live Tracking View */}
            {isRecording && (
              <div className="p-4 space-y-4">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-6 text-center">
                  <div className="text-4xl font-mono font-bold text-white mb-2">
                    {formatDuration(Math.floor((Date.now() - (recordingData.startTime || Date.now())) / 1000))}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <div className="text-2xl font-bold text-white">
                        {(recordingData.distance / 1000).toFixed(2)}
                      </div>
                      <div className="text-xs text-text-secondary">km</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-emerald-400">
                        AQI {airQuality?.aqi || '--'}
                      </div>
                      <div className="text-xs text-text-secondary">current</div>
                    </div>
                  </div>
                  <div className="mt-4 text-xs text-text-secondary">
                    {recordingData.route.length} GPS points recorded
                  </div>
                </div>

                <button
                  onClick={handleStopTracking}
                  className="w-full py-4 rounded-xl bg-red-500 text-white font-bold text-lg"
                >
                  ⏹️ Stop Recording
                </button>
              </div>
            )}

            {/* Manual Entry Form */}
            {!isRecording && (
              <div className="p-4 space-y-4">
                {/* Mode Toggle */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setMode('manual')}
                    className={cn(
                      'flex-1 py-3 rounded-xl font-medium transition-all',
                      mode === 'manual'
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-slate-800/50 text-text-secondary'
                    )}
                  >
                    ✏️ Manual Entry
                  </button>
                  <button
                    onClick={handleStartTracking}
                    className={cn(
                      'flex-1 py-3 rounded-xl font-medium transition-all',
                      'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white'
                    )}
                  >
                    📍 Start GPS Tracking
                  </button>
                </div>

                {/* Activity Type */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Activity Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {activityTypes.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setFormData((prev) => ({ ...prev, activity_type: type.value }))}
                        className={cn(
                          'px-4 py-2 rounded-full text-sm transition-all',
                          formData.activity_type === type.value
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-800/50 text-text-secondary hover:text-white'
                        )}
                      >
                        {type.emoji} {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Your Display Name *
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your name or nickname"
                    maxLength={30}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Activity Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Morning Run"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>

                {/* Distance */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Distance (km) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.distance_km}
                    onChange={(e) => setFormData((prev) => ({ ...prev, distance_km: e.target.value }))}
                    placeholder="5.00"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Duration *
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <input
                        type="number"
                        value={formData.hours}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hours: e.target.value }))}
                        placeholder="0"
                        className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white text-center focus:border-emerald-500/50 focus:outline-none"
                      />
                      <div className="text-xs text-text-secondary text-center mt-1">hours</div>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={formData.minutes}
                        onChange={(e) => setFormData((prev) => ({ ...prev, minutes: e.target.value }))}
                        placeholder="30"
                        className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white text-center focus:border-emerald-500/50 focus:outline-none"
                      />
                      <div className="text-xs text-text-secondary text-center mt-1">minutes</div>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={formData.seconds}
                        onChange={(e) => setFormData((prev) => ({ ...prev, seconds: e.target.value }))}
                        placeholder="0"
                        className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white text-center focus:border-emerald-500/50 focus:outline-none"
                      />
                      <div className="text-xs text-text-secondary text-center mt-1">seconds</div>
                    </div>
                  </div>
                </div>

                {/* Elevation */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Elevation Gain (m)
                  </label>
                  <input
                    type="number"
                    value={formData.elevation_gain}
                    onChange={(e) => setFormData((prev) => ({ ...prev, elevation_gain: e.target.value }))}
                    placeholder="0"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:border-emerald-500/50 focus:outline-none"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="How did your run feel?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:border-emerald-500/50 focus:outline-none resize-none"
                  />
                </div>

                {/* Visibility */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Visibility
                  </label>
                  <div className="flex gap-2">
                    {[
                      { value: 'public' as const, label: '🌍 Public' },
                      { value: 'followers' as const, label: '👥 Followers' },
                      { value: 'private' as const, label: '🔒 Private' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setFormData((prev) => ({ ...prev, visibility: opt.value }))}
                        className={cn(
                          'flex-1 py-2 rounded-xl text-sm transition-all',
                          formData.visibility === opt.value
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-slate-800/50 text-text-secondary'
                        )}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo Upload */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Add Photo <span className="text-text-secondary font-normal">(optional)</span>
                  </label>
                  {/* Hidden inputs for camera and gallery */}
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                  />
                  {photoPreview ? (
                    /* Photo Preview with Template or Original */
                    <div className="relative">
                      {photoTemplate === 'blurpie' ? (
                        /* Blurp.ie Template Preview - Adaptive to orientation */
                        <div className={cn(
                          'relative rounded-2xl overflow-hidden shadow-xl',
                          photoOrientation === 'portrait' ? 'aspect-[3/4]' : photoOrientation === 'square' ? 'aspect-square' : 'aspect-video'
                        )}>
                          <img
                            src={photoPreview}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                          {/* Top gradient overlay */}
                          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />

                          {/* Top bar - Blurp.ie branding */}
                          <div className="absolute top-0 left-0 right-0 p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-700/90 rounded-xl backdrop-blur-sm">
                                <img src="/logo.png" alt="Blurp.ie" className="w-7 h-7 object-contain" />
                                <div>
                                  <p className="text-xs font-bold text-white tracking-wide">BLURP.IE</p>
                                  <p className="text-[7px] text-white/80 uppercase tracking-widest">Better Air, Better Runs</p>
                                </div>
                              </div>
                              {/* Live AQI Indicator */}
                              {airQuality && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-emerald-500/30">
                                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                                  <span className="text-xs font-bold text-emerald-400">AQI {airQuality.aqi}</span>
                                </div>
                              )}
                            </div>
                            {/* Air Quality Details */}
                            {airQuality && (
                              <div className="flex gap-3 mt-2">
                                <div className="flex items-center gap-1 text-[9px] text-cyan-400">
                                  <span>☁️</span>
                                  <span>PM2.5: {airQuality.pm25?.value || '--'}µg</span>
                                </div>
                                <div className="flex items-center gap-1 text-[9px] text-emerald-400">
                                  <span>🌡️</span>
                                  <span>{airQuality.dominantPollutant || 'Good'}</span>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Bottom stats overlay */}
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            {/* Runner info */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-lg">
                                <span className="text-base">🏃</span>
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-white">{displayName || 'Runner'}</p>
                                <p className="text-[9px] text-emerald-400/80">{formData.title || 'Activity'}</p>
                              </div>
                            </div>
                            {/* Stats grid */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center p-2 bg-black/40 backdrop-blur-sm rounded-xl border border-white/10">
                                <div className="text-lg font-bold text-white">{formData.distance_km || '0'}</div>
                                <div className="text-[8px] text-emerald-400 uppercase tracking-wider">km</div>
                              </div>
                              <div className="text-center p-2 bg-black/40 backdrop-blur-sm rounded-xl border border-white/10">
                                <div className="text-lg font-bold text-white">
                                  {formData.minutes || '0'}:{(formData.seconds || '0').padStart(2, '0')}
                                </div>
                                <div className="text-[8px] text-emerald-400 uppercase tracking-wider">time</div>
                              </div>
                              <div className="text-center p-2 bg-black/40 backdrop-blur-sm rounded-xl border border-emerald-500/30">
                                <div className="text-lg font-bold text-emerald-400">
                                  {formData.distance_km && formData.minutes
                                    ? (parseFloat(formData.minutes) / parseFloat(formData.distance_km || '1')).toFixed(1)
                                    : '--'}
                                </div>
                                <div className="text-[8px] text-emerald-400 uppercase tracking-wider">pace</div>
                              </div>
                            </div>
                          </div>

                          {/* Bottom gradient accent line */}
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500" />
                        </div>
                      ) : (
                        /* Original Photo Preview - Adaptive to orientation */
                        <div className={cn(
                          'rounded-xl overflow-hidden',
                          photoOrientation === 'portrait' ? 'aspect-[3/4]' : photoOrientation === 'square' ? 'aspect-square' : 'aspect-video'
                        )}>
                          <img
                            src={photoPreview}
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <button
                        onClick={removePhoto}
                        className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center text-white hover:bg-black/80"
                      >
                        ✕
                      </button>
                      {/* Bottom action buttons */}
                      <div className="absolute bottom-2 right-2 flex gap-2">
                        {/* Share/Save button */}
                        <button
                          onClick={handleSharePhoto}
                          disabled={isSharing}
                          className="px-3 py-1.5 bg-emerald-500/90 rounded-full text-xs text-white hover:bg-emerald-600 flex items-center gap-1 disabled:opacity-50"
                        >
                          {isSharing ? (
                            <>
                              <span className="animate-spin">⏳</span> Sharing...
                            </>
                          ) : (
                            <>
                              <span>📤</span> Share
                            </>
                          )}
                        </button>
                        {/* Toggle between Template and Original */}
                        <button
                          onClick={() => setPhotoTemplate(photoTemplate === 'blurpie' ? 'original' : 'blurpie')}
                          className="px-3 py-1.5 bg-black/60 rounded-full text-xs text-white hover:bg-black/80 flex items-center gap-1"
                        >
                          {photoTemplate === 'blurpie' ? (
                            <>
                              <span>🖼️</span> Original
                            </>
                          ) : (
                            <>
                              <span>✨</span> Template
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : showPhotoOptions ? (
                    <div className="space-y-2">
                      {/* Camera Option */}
                      <button
                        type="button"
                        onClick={openCamera}
                        className="w-full p-4 rounded-xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 text-white hover:from-emerald-500/30 hover:to-cyan-500/30 transition-all flex items-center gap-3"
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                          <span className="text-2xl">📸</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Camera</p>
                          <p className="text-xs text-emerald-400">Take a photo now</p>
                        </div>
                      </button>
                      {/* Gallery Option */}
                      <button
                        type="button"
                        onClick={openGallery}
                        className="w-full p-4 rounded-xl bg-slate-800/50 border border-glass-border text-white hover:bg-slate-700/50 transition-all flex items-center gap-3"
                      >
                        <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                          <span className="text-2xl">🖼️</span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Photo Library</p>
                          <p className="text-xs text-text-secondary">Choose from gallery</p>
                        </div>
                      </button>
                      {/* Cancel */}
                      <button
                        type="button"
                        onClick={() => setShowPhotoOptions(false)}
                        className="w-full py-2 text-sm text-text-secondary hover:text-white transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPhotoOptions(true)}
                      className="w-full p-4 rounded-xl border-2 border-dashed border-glass-border text-text-secondary hover:border-emerald-500/50 hover:text-white transition-colors flex items-center justify-center gap-2"
                    >
                      <span>📷</span>
                      <span>Tap to add photo</span>
                    </button>
                  )}
                </div>

                {/* Current AQI */}
                {airQuality && (
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-cyan-400">Current Air Quality</span>
                      <span className="text-white font-bold">AQI {airQuality.aqi}</span>
                    </div>
                    <p className="text-[10px] text-text-secondary mt-1">
                      This will be attached to your activity
                    </p>
                  </div>
                )}

                {/* Share Preview Card */}
                <div>
                  <div className="text-xs text-text-secondary mb-3 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                    Share Preview
                  </div>
                  <div className="relative overflow-hidden rounded-2xl shadow-xl">
                    {/* Background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#050a0f] via-[#0a1520] to-[#050a0f]" />
                    <div className="absolute -top-20 -right-20 w-40 h-40 bg-emerald-500/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl" />

                    {/* Card content */}
                    <div className="relative p-5">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <img src="/logo.png" alt="Blurp.ie" className="w-8 h-8 object-contain" />
                          <div>
                            <h3 className="text-sm font-bold text-emerald-400">BLURP.IE</h3>
                            <p className="text-[8px] text-emerald-500/60 uppercase tracking-widest">Better Air, Better Runs</p>
                          </div>
                        </div>
                        {airQuality && (
                          <div className="px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                            <span className="text-[10px] font-bold text-emerald-400">AQI {airQuality.aqi}</span>
                          </div>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="text-center p-2 bg-white/5 rounded-xl">
                          <div className="text-lg font-bold text-white">{formData.distance_km || '0'}</div>
                          <div className="text-[10px] text-text-secondary">km</div>
                        </div>
                        <div className="text-center p-2 bg-white/5 rounded-xl">
                          <div className="text-lg font-bold text-white">
                            {formData.minutes || '0'}:{(formData.seconds || '0').padStart(2, '0')}
                          </div>
                          <div className="text-[10px] text-text-secondary">time</div>
                        </div>
                        <div className="text-center p-2 bg-white/5 rounded-xl">
                          <div className="text-lg font-bold text-emerald-400">
                            {formData.distance_km && formData.minutes
                              ? (parseFloat(formData.minutes) / parseFloat(formData.distance_km || '1')).toFixed(1)
                              : '--'}
                          </div>
                          <div className="text-[10px] text-text-secondary">pace</div>
                        </div>
                      </div>

                      {/* Title & Runner */}
                      <div className="flex items-center justify-between pt-3 border-t border-white/10">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                            <span>🏃</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{displayName || 'Your Name'}</p>
                            <p className="text-[10px] text-text-secondary">{formData.title || 'Activity Title'}</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-emerald-400">blurp.ie</p>
                      </div>
                    </div>

                    {/* Bottom gradient line */}
                    <div className="h-1 bg-gradient-to-r from-emerald-500 via-cyan-500 to-emerald-500" />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className={cn(
                    'w-full py-4 rounded-xl font-bold text-lg',
                    'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white',
                    'hover:opacity-90 transition-opacity',
                    isSaving && 'opacity-60 cursor-not-allowed'
                  )}
                >
                  {isSaving ? 'Saving...' : 'Save Activity'}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default RecordRun;
